
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import type { Profile } from '@/types/auth';

export const useProfile = (user: User | null) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchProfile = async () => {
      if (!user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select(`
            is_student,
            is_trainer,
            id,
            subscription_plan,
            subscription_end_date,
            subscription_status,
            subscription_tier,
            first_name,
            last_name,
            phone,
            address,
            avatar_url,
            bio,
            public_url,
            about_me,
            training_types,
            specialties,
            instagram_url,
            facebook_url,
            country,
            city,
            is_onboarded
          `)
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (mounted && data) {
          // Garantir que todos os campos opcionais estão presentes mesmo que nulos
          const fullProfile: Profile = {
            is_student: data.is_student || false,
            is_trainer: data.is_trainer || false,
            id: data.id,
            subscription_plan: data.subscription_plan,
            subscription_end_date: data.subscription_end_date,
            subscription_status: data.subscription_status as 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete',
            subscription_tier: data.subscription_tier,
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            email: user.email || "",
            phone: data.phone || null,
            address: data.address || null,
            avatar_url: data.avatar_url || null,
            bio: data.bio || null,
            public_url: data.public_url || null,
            about_me: data.about_me || null,
            training_types: data.training_types || null,
            specialties: data.specialties || null,
            instagram_url: data.instagram_url || null,
            facebook_url: data.facebook_url || null,
            country: data.country || null,
            city: data.city || null,
            is_onboarded: data.is_onboarded ?? false
          };
          setProfile(fullProfile);
        }
      } catch (error: any) {
        console.error('Error fetching profile:', error);
        toast.error('Erro ao carregar perfil');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      mounted = false;
    };
  }, [user]);

  return { profile, loading };
};
